import { BRAND_IDS } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useOpsCustomerScopeContext } from '../api/customerScope.ts';
import { useT } from '../i18n/index.ts';
import { Chip, Select, Skeleton } from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';
import { brandLabel } from './BrandChip.tsx';

/**
 * Server-determined brand context for the sales surfaces (epic #1030
 * E1-S4, #1040). Pool / workbench / ownership mount this next to their
 * page header:
 *
 *   - sales sessions see the profile brand plus whether the book is
 *     self-scoped (customer manager) or brand-wide (sales lead) — the
 *     values come from `GET /v1/ops/customers/context`, never from
 *     client-side state;
 *   - admin sees a cross-brand chip and (when the page wires
 *     `brandFilter` / `onBrandFilterChange`) a brand filter select;
 *   - finance (#1958) sees the same cross-brand chip without a filter: the
 *     reads are unscoped, but the session works one customer at a time;
 *   - a sales user without a profile sees the fail-closed hint that the
 *     scoped list routes will also return.
 */

/** Radix Select forbids empty-string values; "all brands" travels as this. */
const ALL_BRANDS_SENTINEL = 'all';

export function BrandScopeBanner({
  testIdPrefix,
  brandFilter,
  onBrandFilterChange,
}: {
  readonly testIdPrefix: string;
  /** Current admin brand filter ('' = all brands). Ignored for sales sessions. */
  readonly brandFilter?: string;
  readonly onBrandFilterChange?: (brand: string) => void;
}): JSX.Element | null {
  const t = useT();
  const context = useOpsCustomerScopeContext();

  if (context.isPending) {
    return (
      <Skeleton
        className="h-6 w-40 rounded-full"
        testId={`${testIdPrefix}-brand-scope-loading`}
      />
    );
  }

  if (context.isError) {
    if (context.error instanceof OpsApiError && context.error.code === 'FORBIDDEN') {
      return (
        <Chip tone="warn" testId={`${testIdPrefix}-brand-scope-no-profile`}>
          {t('brand_scope.no_profile')}
        </Chip>
      );
    }
    return null;
  }

  const scope = context.data.scope;
  if (scope.kind === 'admin') {
    const filterWired = onBrandFilterChange !== undefined;
    const options: readonly SelectOption[] = [
      { value: ALL_BRANDS_SENTINEL, label: t('brand_scope.filter.all') },
      ...BRAND_IDS.map((brand) => ({ value: brand, label: brandLabel(brand) })),
    ];
    return (
      <div className="flex items-center gap-2">
        <Chip tone="neutral" testId={`${testIdPrefix}-brand-scope-admin`}>
          {t('brand_scope.cross_brand')}
        </Chip>
        {filterWired ? (
          <Select
            value={
              brandFilter !== undefined && brandFilter.length > 0
                ? brandFilter
                : ALL_BRANDS_SENTINEL
            }
            onValueChange={(value) =>
              onBrandFilterChange(value === ALL_BRANDS_SENTINEL ? '' : value)
            }
            options={options}
            ariaLabel={t('brand_scope.filter.aria_label')}
            testId={`${testIdPrefix}-brand-filter`}
            className="w-40"
          />
        ) : null}
      </div>
    );
  }

  // Finance session (#1958): cross-brand like admin, but read-only — no brand
  // filter, because a finance manager reaches these reads to check the customer
  // behind a credit application, not to work a book.
  if (scope.kind === 'finance') {
    return (
      <Chip tone="neutral" testId={`${testIdPrefix}-brand-scope-finance`}>
        {t('brand_scope.cross_brand')}
      </Chip>
    );
  }

  // Sales session: the brand is the server-side profile brand (never null
  // for kind === 'sales'; guard defensively for the wire shape anyway).
  const label =
    scope.brand === null
      ? t('brand_scope.cross_brand')
      : scope.self_scoped
        ? t('brand_scope.self_scoped', { brand: brandLabel(scope.brand) })
        : t('brand_scope.brand_wide', { brand: brandLabel(scope.brand) });
  return (
    <Chip tone="signal" testId={`${testIdPrefix}-brand-scope-sales`}>
      {label}
    </Chip>
  );
}
